/**
 * Markup MODAL — the large, centered redline/comment table (Emerson
 * 2026-08-04). Opened from the "markup" button on a linked Google Doc card;
 * the document itself stays rendered in pane-3 underneath. Replaces the old
 * cramped in-pane-3 table for documents — pane-3 is ~⅓ width, which forced
 * hard truncation. Here the rows are legible at rest (text wraps, no
 * click-to-expand) with a real Author column.
 *
 * Row/CSV/TSV logic is shared with the in-pane MarkupTable (buildRows,
 * effectiveLoc, rowsToCsv/Tsv, ActionWord) so the two never drift.
 *
 * Close via ✕, Esc, or a backdrop click.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Download, Copy, Check, X } from 'lucide-react'
import {
  buildRows, rowsToCsv, rowsToTsv, ActionWord, effectiveLoc, type MarkupRow,
} from './MarkupTable'
import { workProduct, type WorkProductType } from '../api'
import PullDialog, { PULL_TYPES } from './PullDialog'

/** Shorten the over-fancy "scrivener's fix" characterization to just
 *  "scrivener" (Emerson 2026-08-05). Other type tags render verbatim. */
function prettyType(t: string): string {
  return /scrivener/i.test(t) ? 'scrivener' : t
}

type ColKey = 'where' | 'type' | 'markup' | 'author'

type Props = {
  filename: string
  artifactId: number
  redlines: Array<Record<string, unknown>>
  comments: Array<Record<string, unknown>>
  onClose: () => void
}

export default function MarkupModal({ filename, artifactId, redlines, comments, onClose }: Props) {
  const rows = useMemo(() => buildRows(redlines, comments), [redlines, comments])
  const [copied, setCopied] = useState(false)

  // MARKUP Pull — save a redline/comment as Work Product through the SAME
  // dialog as the email Pull (shared PullDialog): multi-select type tags +
  // passage, PLUS a WHERE row carrying the document location the email context
  // has no analog for. There's no text selection here — clicking a MARKUP cell
  // IS the selection (the row's text = `exact`). Pulled rows get a dot this
  // session; persistent pre-load seeds from the artifact's existing excerpts.
  // Emerson 2026-08-05; aligned to the Pull feature 2026-08-14.
  const [pullRow, setPullRow] = useState<MarkupRow | null>(null)
  const [pullTypes, setPullTypes] = useState<Set<WorkProductType>>(new Set())
  const [pullBusy, setPullBusy] = useState(false)
  const [pullExpanded, setPullExpanded] = useState(false)
  const [pulledKeys, setPulledKeys] = useState<Set<string>>(new Set())
  const pullExact = (r: MarkupRow) => r.text || r.anchor || ''
  // WHERE = the document location (derived paragraph/section, or the quoted
  // anchor). Dropped when there's none ('—') or when it would merely repeat
  // the passage (an anchor-only comment whose text we already show as `exact`).
  const pullWhere = (r: MarkupRow) => {
    const loc = effectiveLoc(r)
    return loc === '—' || loc === pullExact(r) ? '' : loc
  }
  const openPull = (r: MarkupRow) => { setPullRow(r); setPullTypes(new Set()); setPullExpanded(false) }
  const togglePullType = (slug: WorkProductType) =>
    setPullTypes((prev) => {
      const next = new Set(prev)
      next.has(slug) ? next.delete(slug) : next.add(slug)
      return next
    })
  const savePull = async (note: string) => {
    if (!pullRow || pullBusy) return
    setPullBusy(true)
    try {
      await workProduct.mark(artifactId, {
        excerpt: {
          exact: pullExact(pullRow),
          where: pullWhere(pullRow),
          note: note.trim(),
          types: PULL_TYPES.map((t) => t.slug).filter((s) => pullTypes.has(s)),
        },
      })
      setPulledKeys((prev) => new Set(prev).add(pullRow.key))
      setPullRow(null)
    } catch { /* leave dialog open to retry */ } finally { setPullBusy(false) }
  }

  // Persist the trained dot: on open, seed trainedKeys from the artifact's
  // existing WorkProduct excerpts (matched by the markup text we saved as
  // `exact`). Merge so a just-saved row isn't clobbered by a slow fetch.
  const pullKeyText = (r: MarkupRow) => (r.text || r.anchor || '').slice(0, 4000)
  useEffect(() => {
    let cancelled = false
    workProduct.list(true).then((items) => {
      if (cancelled) return
      const mine = items.find((it) => it.artifact === artifactId)
      const exacts = new Set((mine?.excerpts || []).map((e) => e.exact))
      const seed = rows.filter((r) => exacts.has(pullKeyText(r))).map((r) => r.key)
      if (seed.length) setPulledKeys((prev) => new Set([...prev, ...seed]))
    }).catch(() => { /* best effort — the dot just won't pre-fill */ })
    return () => { cancelled = true }
  }, [artifactId, rows])

  // Resizable columns (Emerson 2026-08-05): the right border of WHERE and MARKUP
  // is a hidden col-resize handle — no visual change, discovered via the cursor.
  // Dragging transfers width to the right neighbor (Type / Author). Session-only.
  const [cols, setCols] = useState({ where: 30, type: 14, markup: 42, author: 14 })
  const tableRef = useRef<HTMLTableElement>(null)
  const startResize = (
    clientX: number, col: ColKey, next: ColKey,
    minCol: number, minNext: number,
  ) => {
    const tw = tableRef.current?.clientWidth || 1
    const start = { ...cols }
    const onMove = (ev: PointerEvent) => {
      const d = ((ev.clientX - clientX) / tw) * 100
      let a = start[col] + d
      let b = start[next] - d
      if (a < minCol) { b -= minCol - a; a = minCol }
      if (b < minNext) { a -= minNext - b; b = minNext }
      setCols((c) => ({ ...c, [col]: a, [next]: b }))
    }
    const onUp = () => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
    }
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
  }

  // Sort: document order by default; clicking the Author or Type header sorts
  // by it, cycling asc → desc → back to document order (Emerson 2026-08-05).
  const [sort, setSort] = useState<{ by: 'doc' | 'author' | 'type'; dir: 'asc' | 'desc' }>(
    { by: 'doc', dir: 'asc' })
  const toggleSort = (col: 'author' | 'type') =>
    setSort((p) =>
      p.by !== col ? { by: col, dir: 'asc' }
        : p.dir === 'asc' ? { by: col, dir: 'desc' }
          : { by: 'doc', dir: 'asc' })
  const sortArrow = (col: 'author' | 'type') =>
    sort.by === col ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''
  const sortedRows = useMemo(() => {
    if (sort.by === 'doc') return rows
    const dir = sort.dir === 'asc' ? 1 : -1
    // blanks-last, direction-aware string compare; 0 when equal so callers
    // can chain with || onto the document-order tiebreak.
    const cmp = (a: string, b: string) => {
      if (!a && b) return 1          // blanks sort last, both directions
      if (a && !b) return -1
      if (a < b) return -1 * dir
      if (a > b) return 1 * dir
      return 0
    }
    // 'type' sorts by the characterization CLASS (scrivener / substantive / …),
    // regardless of redline-vs-comment. Key on the DISPLAYED prettyType label,
    // NOT raw r.note: prettyType collapses several raw strings ("typo
    // (scrivener)", "scrivener's fix") into one class, so keying on the raw
    // text scattered those rows apart even though they show the same label —
    // the "sorts some, breaks, sorts again" bug. Emerson 2026-08-11.
    const key = (r: MarkupRow) =>
      (sort.by === 'author' ? r.author : prettyType(r.note)).toLowerCase()
    return [...rows].sort((a, b) => cmp(key(a), key(b)) || (a.sortKey - b.sortKey))
  }, [rows, sort])
  // Export mirrors the visible order + the shortened type label.
  const exportRows = useMemo(
    () => sortedRows.map((r) => ({ ...r, note: prettyType(r.note) })),
    [sortedRows])

  const nComments = rows.filter((r) => r.source === 'comment').length
  const nRedlines = rows.filter((r) => r.source === 'redline').length

  // Esc closes. Lock body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (pullRow) setPullRow(null)   // Esc backs out of the Pull dialog first
      else onClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose, pullRow])

  const onDownload = () => {
    const csv = rowsToCsv(filename, exportRows)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${filename.replace(/\.[a-z0-9]+$/i, '')}-markup.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(rowsToTsv(filename, exportRows))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* clipboard denied — silent, matches MarkupTable */ }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Markup for ${filename}`}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(0,0,0,0.55)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col rounded-lg border me-border shadow-2xl bg-[color:var(--me-surface)]"
        style={{ width: 'min(1100px, 92vw)', maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b me-border shrink-0">
          <div className="min-w-0">
            <div className="me-text font-medium text-[13px] truncate">{filename || '(unnamed document)'}</div>
            <div className="me-text-mute text-[11px] me-mono mt-0.5">
              {nComments} comment{nComments === 1 ? '' : 's'} · {nRedlines} redline{nRedlines === 1 ? '' : 's'}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={onDownload} className="me-btn me-btn-outline" title="Download as CSV" disabled={rows.length === 0}>
              <Download size={11} /> download
            </button>
            <button onClick={onCopy} className="me-btn me-btn-outline" title="Copy as TSV (paste into a spreadsheet)" disabled={rows.length === 0}>
              {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? 'Copied' : 'Copy'}
            </button>
            <button onClick={onClose} className="me-btn me-btn-outline" title="Close (Esc)" aria-label="Close">
              <X size={13} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="me-scroll overflow-auto">
          {rows.length === 0 ? (
            <div className="p-6 text-[12.5px] me-text-mute italic">No redlines or comments on this document.</div>
          ) : (
            <table ref={tableRef} className="w-full table-fixed text-[12.5px]">
              <colgroup>
                <col style={{ width: cols.where + '%' }} />{/* WHERE */}
                <col style={{ width: cols.type + '%' }} />{/* TYPE — pip + characterization */}
                <col style={{ width: cols.markup + '%' }} />{/* MARKUP */}
                <col style={{ width: cols.author + '%' }} />{/* AUTHOR */}
              </colgroup>
              <thead>
                <tr className="sticky top-0 z-10 bg-[color:var(--me-panel)] me-text-mute uppercase tracking-wider me-mono text-[10px]">
                  <th className="relative text-left px-4 py-2">
                    Where
                    <span
                      onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); startResize(e.clientX, 'where', 'markup', 12, 20) }}
                      className="absolute top-0 right-0 h-full"
                      style={{ width: 8, cursor: 'col-resize', touchAction: 'none' }}
                      aria-hidden
                    />
                  </th>
                  <th
                    className="text-left px-3 py-2 cursor-pointer select-none hover:me-text whitespace-nowrap"
                    onClick={() => toggleSort('type')}
                    title="Sort by type"
                  >
                    Type{sortArrow('type')}
                  </th>
                  <th className="text-left px-3 py-2">Markup</th>
                  <th
                    className="text-left px-3 py-2 cursor-pointer select-none hover:me-text whitespace-nowrap"
                    onClick={() => toggleSort('author')}
                    title="Sort by author"
                  >
                    Author{sortArrow('author')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => {
                  const isAnchor = r.loc === '—' && !!r.anchor
                  // No numeric location AND no quoted anchor — e.g. Google Docs
                  // @-mention / reply comments that aren't tied to a text range.
                  // Show a muted "no anchor" instead of a bare em-dash so an
                  // empty Where reads as intentionally-empty, not failed-to-load.
                  const isNoAnchor = r.loc === '—' && !r.anchor
                  return (
                    <tr key={r.key} className="border-t me-border align-top hover:bg-[color:var(--me-panel)]">
                      <td
                        className={`px-4 py-2.5 me-text-dim ${isAnchor || isNoAnchor ? 'italic' : 'me-mono'}`}
                        style={{ wordBreak: 'break-word' }}
                      >
                        {isAnchor ? `“${r.anchor}”` : isNoAnchor ? 'no anchor' : r.loc}
                      </td>
                      {/* Type = a colored pip (redline = red, comment = blue —
                          the comment/redline distinction is carried by color
                          alone) + the characterization tag from
                          extract_attachment_markup (scrivener's fix / drafting
                          note / …). Em-dash until the doc is enriched. */}
                      <td className="px-3 py-2.5 me-text-dim text-[11.5px]">
                        <span className="flex items-start gap-1.5">
                          <span
                            aria-hidden
                            title={r.source}
                            style={{
                              width: 7, height: 7, borderRadius: '50%', marginTop: 5, flexShrink: 0,
                              background: r.source === 'redline' ? '#d0453b' : '#4a8fd0',
                            }}
                          />
                          <span style={{ wordBreak: 'break-word' }}>
                            {r.note ? prettyType(r.note) : <span className="me-text-mute">—</span>}
                          </span>
                        </span>
                      </td>
                      {/* MARKUP — trainable (Emerson 2026-08-05). Clicking the
                          cell opens the note dialog; a hover affordance signals
                          it; trained rows carry a green dot. */}
                      <td
                        className="group relative px-3 py-2.5 me-text leading-relaxed cursor-pointer hover:bg-[color:var(--me-panel)]"
                        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        onClick={() => openPull(r)}
                      >
                        {pulledKeys.has(r.key) && (
                          <span aria-hidden title="pulled"
                            style={{ position: 'absolute', left: 3, top: 13, width: 6, height: 6, borderRadius: '50%', background: '#3f9e5a' }} />
                        )}
                        {r.action && <ActionWord action={r.action} />}
                        {r.action && ' '}
                        {r.text || <em className="me-text-mute">(empty)</em>}
                        <span className="pointer-events-none absolute right-2 bottom-1.5 inline-flex items-center gap-1 me-mono text-[10px] me-text-mute opacity-0 group-hover:opacity-100">
                          {pulledKeys.has(r.key) ? 'pulled' : 'pull'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 me-text-dim" style={{ wordBreak: 'break-word' }}>
                        {r.author || <span className="me-text-mute">—</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* MARKUP Pull dialog — the SAME PullDialog as the email Pull, opened by
          clicking a MARKUP cell. Passage = the redline/comment text; WHERE =
          the document location (markup-only context). Esc / backdrop / Cancel
          back out without closing the parent markup modal. Emerson 2026-08-05;
          aligned to the Pull feature 2026-08-14. */}
      {pullRow && (
        <PullDialog
          types={pullTypes}
          onToggleType={togglePullType}
          where={pullWhere(pullRow) || null}
          onCancel={() => setPullRow(null)}
          onSave={savePull}
          busy={pullBusy}
        >
          <div
            className={`text-[12px] italic me-text ${pullExpanded ? '' : 'line-clamp-3'}`}
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {pullExact(pullRow) || <span className="me-text-mute">(empty markup)</span>}
          </div>
          {pullExact(pullRow).length > 160 && (
            <button
              type="button"
              onClick={() => setPullExpanded((v) => !v)}
              className="mt-1 text-[11px] me-text-mute hover:me-text underline"
            >
              {pullExpanded ? 'less' : 'more'}
            </button>
          )}
        </PullDialog>
      )}
    </div>
  )
}
